import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { fmtDate, fmtTime } from '../lib/format';

// ───────── Toast ─────────
type ToastFn = (msg: string, kind?: 'ok' | 'err') => void;
const ToastCtx = createContext<ToastFn>(() => undefined);
export const useToast = () => useContext(ToastCtx);

// ───────── Lightbox ─────────
export interface LightboxPhoto {
  url: string; id: string; setor: string; legenda: string | null; isNc: boolean; stamp: string; author: string;
  /** Se presente, mostra "Excluir foto" (admin/nutricionista). */
  onDelete?: () => Promise<void>;
}
const LightboxCtx = createContext<(p: LightboxPhoto | null) => void>(() => undefined);
export const useLightbox = () => useContext(LightboxCtx);

export function photoToLightbox(p: { id: string; fullUrl: string; setor: string; legenda: string | null; isNc: boolean; capturedAt: number; authorNome: string }): LightboxPhoto {
  return { id: p.id, url: p.fullUrl, setor: p.setor, legenda: p.legenda, isNc: p.isNc, stamp: `${fmtDate(p.capturedAt)} · ${fmtTime(p.capturedAt)}`, author: p.authorNome };
}

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<{ msg: string; kind: 'ok' | 'err' } | null>(null);
  const [photo, setPhotoState] = useState<LightboxPhoto | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const setPhoto = useCallback((p: LightboxPhoto | null) => { setConfirmDel(false); setPhotoState(p); }, []);
  const timer = useRef<number | undefined>(undefined);
  const show = useCallback<ToastFn>((msg, kind = 'ok') => {
    setToast({ msg, kind });
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast(null), kind === 'err' ? 4200 : 2600);
  }, []);

  useEffect(() => {
    if (!photo) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setPhoto(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photo]);

  return (
    <ToastCtx.Provider value={show}>
      <LightboxCtx.Provider value={setPhoto}>
        {children}
        {toast && createPortal(<div className={`toast ${toast.kind === 'err' ? 'err' : ''}`} role="status" aria-live="polite">{toast.msg}</div>, document.body)}
        {photo &&
          createPortal(
            <div className="lightbox" onClick={() => setPhoto(null)} role="dialog" aria-modal="true" aria-label="Foto">
              <div className="in">
                <div className="pic" style={{ backgroundImage: `url("${photo.url}")` }} />
                <div className="info" onClick={(e) => e.stopPropagation()}>
                  <div className="row wrap gap-6" style={{ marginBottom: 9 }}>
                    <span className="pill upper res">{photo.setor}</span>
                    {photo.isNc && <span className="pill upper new">não conformidade</span>}
                  </div>
                  <div style={{ fontSize: 14.5, lineHeight: 1.5, marginBottom: 10 }}>{photo.legenda || 'Sem legenda'}</div>
                  <div className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.7 }}>{photo.stamp} · {photo.author}</div>
                  <div className="row wrap gap-8" style={{ marginTop: 12 }}>
                    <a className="btn xs" href={photo.url} target="_blank" rel="noreferrer">Abrir original</a>
                    <button className="btn xs" onClick={() => setPhoto(null)}>Fechar</button>
                    {photo.onDelete && (
                      <button
                        className={`btn xs ${confirmDel ? 'danger solid' : 'danger'}`}
                        disabled={deleting}
                        onClick={async () => {
                          if (!confirmDel) return setConfirmDel(true);
                          setDeleting(true);
                          try {
                            await photo.onDelete!();
                            setPhoto(null);
                          } catch (e) {
                            show(e instanceof Error ? e.message : 'Não foi possível excluir', 'err');
                          } finally {
                            setDeleting(false);
                          }
                        }}
                      >
                        {deleting ? 'Excluindo…' : confirmDel ? 'Confirmar exclusão' : 'Excluir foto'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </LightboxCtx.Provider>
    </ToastCtx.Provider>
  );
}
